import type { NetworkData } from '@dynamic-labs-sdk/client';
import { FeeEstimationFailedError } from '@dynamic-labs-sdk/client';
import type { EvmTransactionFeeData } from '@dynamic-labs-sdk/evm';
import { formatEther } from 'viem';
import type { UserOperation } from 'viem/account-abstraction';

import { calculateGasForUserOperation } from '../calculateGasForUserOperation';

type CalculateFeeForUserOperationParams = {
  nativeTokenPriceUsd?: number;
  networkData: NetworkData;
  userOperationData: Pick<
    UserOperation,
    | 'callGasLimit'
    | 'verificationGasLimit'
    | 'preVerificationGas'
    | 'maxFeePerGas'
  >;
};

/**
 * Calculates the estimated fee for a ZeroDev user operation.
 *
 * This function estimates the total gas cost for a user operation by summing
 * all gas components (call, verification, pre-verification) and multiplying
 * by the maximum fee per gas unit.
 *
 * @param params.userOperationData - The user operation data containing gas limits and fees.
 * @param params.networkData - Network configuration (not currently used but kept for consistency).
 * @param params.nativeTokenPriceUsd - Optional USD price of the native token for fee conversion.
 * @returns Fee data including gas estimates and human-readable amounts.
 * @throws {FeeEstimationFailedError} If fee calculation fails.
 */
export const calculateFeeForUserOperation = ({
  userOperationData,
  networkData: _networkData,
  nativeTokenPriceUsd,
}: CalculateFeeForUserOperationParams): EvmTransactionFeeData => {
  try {
    // Calculate total gas cost in wei
    const nativeAmount = calculateGasForUserOperation({ userOperationData });

    // Calculate total gas units
    const gasEstimate =
      userOperationData.callGasLimit +
      userOperationData.verificationGasLimit +
      userOperationData.preVerificationGas;

    // Format to human-readable ETH
    const humanReadableAmount = formatEther(nativeAmount);

    // Calculate USD amount if price data available
    const usdAmount = nativeTokenPriceUsd
      ? (Number(humanReadableAmount) * nativeTokenPriceUsd).toFixed(2)
      : undefined;

    return {
      gasEstimate,
      humanReadableAmount,
      maxFeePerGas: userOperationData.maxFeePerGas,
      nativeAmount,
      usdAmount,
    };
  } catch (error) {
    throw new FeeEstimationFailedError({
      cause: error instanceof Error ? error : undefined,
      message: 'Failed to calculate user operation fee',
    });
  }
};
